// src/App.js
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import DemoGate from "./components/DemoGate";
import "./ui/ui.css";

import Login from "./Login";
import SignupPage from "./pages/SignupPage";
import DashboardLayout from "./components/DashboardLayout";
import DashboardHome from "./pages/DashboardHome";
import ChatPage from "./pages/ChatPage";
import InboxPage from "./pages/InboxPage";
import NotificationsPage from "./pages/NotificationsPage";
import CheckInPage from "./pages/CheckInPage";
import SupportInboxPage from "./pages/SupportInboxPage";
import SupportMyTicketsPage from "./pages/SupportMyTicketsPage";
import ProfilePage from "./pages/ProfilePage";
import PreferencesPage from "./pages/PreferencesPage";
import GamePreview from "./pages/GamePreview";
import UsersPage from "./pages/UsersPage";
import AdminDashboard from "./pages/AdminDashboard";
import InvestorShowcase from "./pages/InvestorShowcase";
import DemoWalkthrough from "./pages/DemoWalkthrough";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";

// ORG pages
import InviteUsersPage from "./pages/org/InviteUsersPage";
import AdminPage from "./pages/org/AdminPage";
import SeatUsagePage from "./pages/org/SeatUsagePage";
import BillingPage from "./pages/org/BillingPage";
import OrgInsightsPage from "./pages/org/OrgInsightsPage";

import { NIAProvider } from "./context/NIAContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PointsProvider } from "./context/PointsContext";
import { ThemeProvider } from "./context/ThemeContext";
import { isRoleAllowed } from "./utils/roleAccess";

function RequireRole({ roles, children }) {
  const { user, loading, effectiveRole } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (roles && !isRoleAllowed(effectiveRole, roles)) return <Navigate to="/dashboard" replace />;
  return children;
}

function RequireActualRole({ roles, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (roles && !isRoleAllowed(user.role, roles)) return <Navigate to="/dashboard" replace />;
  return children;
}

function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <NIAProvider>
        <PointsProvider>
          <ThemeProvider>
          <DemoGate>
            <Router>
              <ScrollToTopOnRouteChange />
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />

                {/* Everything inside the dashboard chrome */}
                <Route element={<DashboardLayout />}>
                  <Route path="/dashboard" element={<DashboardHome />} />
                  <Route
                    path="/chat"
                    element={<RequireRole roles={["member","coach","admin","superadmin","investor"]}><ChatPage /></RequireRole>}
                  />
                  <Route path="/inbox" element={<InboxPage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/checkin" element={<CheckInPage />} />
                  <Route path="/games" element={<GamePreview />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/preferences" element={<PreferencesPage />} />
                  <Route
                    path="/investor"
                    element={<RequireActualRole roles={["superadmin","investor"]}><InvestorShowcase /></RequireActualRole>}
                  />
                  <Route
                    path="/demo"
                    element={<RequireActualRole roles={["superadmin","investor"]}><DemoWalkthrough /></RequireActualRole>}
                  />

                  {/* Role-guarded */}
                  <Route
                    path="/coach"
                    element={<RequireRole roles={["coach","superadmin","investor"]}><AdminDashboard /></RequireRole>}
                  />
                  <Route
                    path="/users"
                    element={<RequireRole roles={["superadmin","investor"]}><UsersPage /></RequireRole>}
                  />
                  <Route
                    path="/support"
                    element={<RequireRole roles={["superadmin"]}><SupportInboxPage /></RequireRole>}
                  />
                  <Route path="/support/my" element={<SupportMyTicketsPage />} />

                  {/* Manage Organisation (nested) — Admin + SuperAdmin */}
                  <Route path="/org">
                    <Route index element={<Navigate to="admin" replace />} />
                    <Route
                      path="admin"
                      element={<RequireRole roles={["admin","superadmin","investor"]}><AdminPage /></RequireRole>}
                    />
                    <Route
                      path="invite"
                      element={<RequireRole roles={["admin","superadmin","investor"]}><InviteUsersPage /></RequireRole>}
                    />
                    <Route
                      path="usage"
                      element={<RequireRole roles={["admin","superadmin","investor"]}><SeatUsagePage /></RequireRole>}
                    />
                    <Route
                      path="billing"
                      element={<RequireRole roles={["admin","superadmin","investor"]}><BillingPage /></RequireRole>}
                    />
                    <Route
                      path="insights"
                      element={<RequireRole roles={["admin","superadmin","investor"]}><OrgInsightsPage /></RequireRole>}
                    />
                  </Route>

                  {/* Back-compat: old /admin -> /org/admin */}
                  <Route path="/admin" element={<Navigate to="/org/admin" replace />} />
                </Route>

                {/* default / fallback */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Router>
            </DemoGate>
          </ThemeProvider>
        </PointsProvider>
      </NIAProvider>
    </AuthProvider>
  );
}
